import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import usePlacesAutocomplete from 'use-places-autocomplete';

import LocationIcon from '@artemis/containers/LocationSearchField/LocationIcon';
import TeamSelectionBox from '@artemis/components/Teams/TeamSelectionBox';
import { useFormatMessage } from '@artemis/integrations/contentful/utils';

import { Wrapper } from '@googlemaps/react-wrapper';
import { useDispatch } from 'react-redux';
import { addToast } from '@artemis/store/toasts/slice';
import { SearchContainer } from './shared';

const ResultsContainer = styled.ul`
  padding: 0;
  margin: 0;
  margin-top: 28px;
  text-align: left;
`;

export const LocationAutocomplete = ({
  className,
  selectedGooglePlaceId,
  onLocationSelected,
}) => {
  const dispatch = useDispatch();
  const {
    value,
    suggestions: { status, data },
    setValue,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: { types: ['address'] },
  });

  const placeholder = useFormatMessage({
    entry: 'teams.addLocationModal.inputPlaceholder',
  });

  useEffect(() => {
    onLocationSelected(null);
  }, [value]);

  const filteredLocations = useMemo(
    () => data?.filter(({ types }) => !types.includes('route')) || [],
    [data],
  );

  const onSelect = async ({ place_id: googlePlaceId }) => {
    try {
      onLocationSelected({ googlePlaceId });
    } catch (e) {
      dispatch(addToast({ toast: { error: { message: 'errors.tryAgain' } } }));
    }
  };

  return (
    <>
      <SearchContainer className={className}>
        <LocationIcon />
        <input
          placeholder={placeholder}
          value={value}
          onChange={e => setValue(e.target.value)}
        />
      </SearchContainer>
      {status === 'OK' && (
        <ResultsContainer>
          {filteredLocations.map(place => (
            <TeamSelectionBox
              key={place.place_id}
              id={place.place_id}
              name={place.description}
              onSelect={() => onSelect(place)}
              selected={selectedGooglePlaceId === place.place_id}
            />
          ))}
        </ResultsContainer>
      )}
    </>
  );
};

const WrappedLocationAutocomplete = props => (
  <Wrapper apiKey={process.env.RT_GOOGLE_MAPS_API_KEY} libraries={['places']}>
    <LocationAutocomplete {...props} />
  </Wrapper>
);

export default WrappedLocationAutocomplete;

LocationAutocomplete.propTypes = {
  className: PropTypes.string,
  selectedGooglePlaceId: PropTypes.string,
  onLocationSelected: PropTypes.func.isRequired,
};
